import React, { useEffect, useRef, useState } from 'react';
import { MeetingStateProvider } from './context/MeetingStateContext';
import { LiveInsightsProvider } from './context/LiveInsightsContext';
import { BannerProvider } from './components/BannerNotification';
import MainView from './components/MainView';
import Header from './components/Header';
import logoImage from '../3 ICON.png';
import { MeetingOverrunOverlay } from './components/RithmoStageView';

const SHARE_CAPTURE_CLASS = 'share-capture-layout';
const SHARE_VIEW_SCREENSHARE_CLASS = 'share-view-screenshare';

// ---------------------------------------------------------------------------
// Wake-word animation preview (localhost only — ?wakePreview=1)
// ---------------------------------------------------------------------------

function WakePreview() {
  const [sim10fps, setSim10fps] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!sim10fps) { setElapsed(0); return; }
    intervalRef.current = setInterval(() => setElapsed(e => e + 100), 100);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [sim10fps]);

  // Scrub the paused CSS animations by driving animationDelay to the current frame.
  // Outer glow has a 400ms phase offset in the CSS, preserved here.
  const pausedAt = (delay: number): React.CSSProperties =>
    sim10fps ? { animationPlayState: 'paused', animationDelay: `-${delay}ms` } : {};

  const logoMask = { '--logo-mask': `url(${logoImage})` } as React.CSSProperties;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(8,8,16,0.76)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="rs-wake-glow--outer" style={pausedAt(Math.max(0, elapsed - 400))} />
      <div className="rs-wake-glow--inner" style={pausedAt(elapsed)} />
      <div className="rs-wake-logo-wrap" style={pausedAt(elapsed)}>
        <img src={logoImage} className="rs-wake-logo" alt="" />
        <div className="rs-wake-logo-wave" style={{ ...logoMask, ...pausedAt(elapsed) }} />
      </div>
      <button
        onClick={() => setSim10fps(s => !s)}
        style={{ position: 'fixed', bottom: 24, right: 24, padding: '8px 18px', background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, cursor: 'pointer', fontSize: 13, letterSpacing: '0.02em' }}
      >
        {sim10fps ? 'Show at 60 fps' : 'Show at 10 fps (meeting)'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Time-glow preview (localhost only — ?timeGlowPreview=1)
// ---------------------------------------------------------------------------

const PREVIEW_DURATION_MS = 8_000; // 15× speed: 2 min compressed to 8s

function TimeGlowPreview() {
  const [startedAt, setStartedAt] = useState(Date.now());
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setProgress(Math.min(1, (Date.now() - startedAt) / PREVIEW_DURATION_MS));
    }, 50);
    return () => clearInterval(id);
  }, [startedAt]);

  const outerStop    = (10 + 100 * progress).toFixed(0);
  const innerOpacity = (0.05 + 0.50 * progress).toFixed(3);
  const hueRotate    = (166 * progress).toFixed(0);
  const brightness   = (1 + 0.15 * progress).toFixed(2);

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0d1117' }}>
      {/* glow layer */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `radial-gradient(circle at center, rgba(225,70,110,${innerOpacity}) 0%, transparent ${outerStop}%)` }}>
        <img src={logoImage} aria-hidden style={{ width: 128, height: 128, objectFit: 'contain', filter: `hue-rotate(${hueRotate}deg) brightness(${brightness})` }} />
      </div>
      {/* debug overlay */}
      <div style={{ position: 'absolute', bottom: 60, left: 24, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace', fontSize: 12 }}>
        <div>progress: {(progress * 100).toFixed(0)}% &nbsp;·&nbsp; {(progress * 120).toFixed(0)}s / 120s</div>
      </div>
      <button
        onClick={() => { setStartedAt(Date.now()); setProgress(0); }}
        style={{ position: 'fixed', bottom: 24, right: 24, padding: '8px 18px', background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}
      >
        Restart
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overrun animation preview (localhost only — ?overrunPreview=1)
// ---------------------------------------------------------------------------

function OverrunPreview() {
  const [overrunMin, setOverrunMin] = useState(3);
  const pulsePhaseRef = useRef(0);
  const [pulseIntensity, setPulseIntensity] = useState(0);

  useEffect(() => {
    const TICK_MS = 100;
    const id = setInterval(() => {
      const period = Math.max(800, 5000 - 4200 * (overrunMin / 30));
      pulsePhaseRef.current = (pulsePhaseRef.current + TICK_MS / period) % 1;
      setPulseIntensity((1 - Math.cos(2 * Math.PI * pulsePhaseRef.current)) / 2);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [overrunMin]);

  const overrunMs = overrunMin * 60_000;
  const tierLabel =
    overrunMin >= 25 ? 'Tier 5 — all effects + fastest pulse' :
    overrunMin >= 20 ? 'Tier 4 — bg breathing' :
    overrunMin >= 15 ? 'Tier 3 — ghost echo' :
    overrunMin >= 10 ? 'Tier 2 — color drifting + glow pulse' :
    overrunMin >= 5  ? 'Tier 1 — logo growing' :
    overrunMin >  0  ? 'Tier 0 — logo pulse + glow' : 'Not overrun';

  const period = overrunMin > 0 ? Math.max(800, 5000 - 4200 * (overrunMin / 30)) : 0;

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0d1117' }}>
      {overrunMs > 0 && <MeetingOverrunOverlay overrunMs={overrunMs} pulseIntensity={pulseIntensity} />}
      <div style={{ position: 'fixed', bottom: 60, left: 24, color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace', fontSize: 12 }}>
        <div>{tierLabel}</div>
        <div style={{ marginTop: 4 }}>
          {overrunMin.toFixed(1)} min over · period {period > 0 ? `${Math.round(period)}ms` : '—'} · intensity {(pulseIntensity * 100).toFixed(0)}%
        </div>
      </div>
      <div style={{ position: 'fixed', bottom: 20, left: 24, right: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap' }}>0 min</span>
        <input
          type="range" min={0} max={30} step={0.5} value={overrunMin}
          onChange={(e) => setOverrunMin(Number(e.target.value))}
          style={{ flex: 1 }}
        />
        <span style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap' }}>30 min</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root app
// ---------------------------------------------------------------------------

function App() {
  const params = new URLSearchParams(window.location.search);
  const isWakePreview = params.get('wakePreview') === '1';
  const isTimeGlowPreview = params.get('timeGlowPreview') === '1';
  const isOverrunPreview = params.get('overrunPreview') === '1';
  const [isShareCapture, setIsShareCapture] = React.useState(false);

  useEffect(() => {
    if (isWakePreview || isTimeGlowPreview || isOverrunPreview) return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('shareCapture') === '1') {
        setIsShareCapture(true);
        document.documentElement.classList.add(SHARE_CAPTURE_CLASS);
        if (params.get('shareView') === 'screenshare' && params.get('meetingId')) {
          document.documentElement.classList.add(SHARE_VIEW_SCREENSHARE_CLASS);
        }
        return () => {
          document.documentElement.classList.remove(SHARE_CAPTURE_CLASS);
          document.documentElement.classList.remove(SHARE_VIEW_SCREENSHARE_CLASS);
        };
      }
    } catch {
      /* ignore */
    }
    return undefined;
  }, [isWakePreview, isTimeGlowPreview, isOverrunPreview]);

  if (isWakePreview) return <WakePreview />;
  if (isTimeGlowPreview) return <TimeGlowPreview />;
  if (isOverrunPreview) return <OverrunPreview />;

  return (
    <BannerProvider maxBanners={3}>
      <MeetingStateProvider>
        <LiveInsightsProvider>
          <div className="container">
            {!isShareCapture && <Header />}
            <MainView />
          </div>
        </LiveInsightsProvider>
      </MeetingStateProvider>
    </BannerProvider>
  );
}

export default App;
